import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  input
} from '@angular/core';
import { OSKIconComponent } from '../../atoms/osk-icon/osk-icon.component';

/**
 * Molecule: <osk-form-field>
 * Wraps any form control (<osk-input>, <osk-select>, <osk-date-picker>, etc.)
 * with label, required asterisk, optional hint, and animated error message.
 */
@Component({
  selector: 'osk-form-field',
  standalone: true,
  imports: [OSKIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-form-field.component.scss',
  template: `
    <div [class]="fieldClasses()">
      @if (label()) {
        <label class="osk-form-field__label" [attr.for]="forId() ?? null">
          <span>{{ label() }}</span>
          @if (required()) {
            <span class="osk-form-field__required" aria-hidden="true">*</span>
          }
          @if (optionalText()) {
            <span class="osk-form-field__optional">{{ optionalText() }}</span>
          }
        </label>
      }

      <div class="osk-form-field__control">
        <ng-content></ng-content>
      </div>

      @if (error()) {
        <div class="osk-form-field__error" role="alert">
          <osk-icon name="alert-circle" [size]="13"></osk-icon>
          <span>{{ error() }}</span>
        </div>
      } @else if (hint()) {
        <div class="osk-form-field__hint">{{ hint() }}</div>
      }
    </div>
  `
})
export class OSKFormFieldComponent {
  readonly label = input<string | undefined>(undefined);
  readonly forId = input<string | undefined>(undefined);
  readonly required = input(false, { transform: booleanAttribute });
  readonly optionalText = input<string | undefined>(undefined);
  readonly hint = input<string | undefined>(undefined);
  readonly error = input<string | undefined>(undefined);

  protected readonly fieldClasses = computed<string>(() => {
    const classes: string[] = ['osk-form-field'];
    if (this.error()) {
      classes.push('osk-form-field--invalid');
    }
    return classes.join(' ');
  });
}
